// Center pane host for the active cell. Handles fetch + loading +
// error + not-found states; defers rendering of the markdown body
// to MarkdownRenderer.
//
// §6.5 swaps the static /content/* fetch for a live POST to
// /agent/render-cell. The agent resolves bindings and produces
// per-investor markdown; the frontend STOPS calling applyBindings
// on the agent response (the agent does it server-side). The
// MarkdownRenderer, directive vocabulary, and loading/error UI
// are unchanged.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MarkdownRenderer } from '@rmint/shell';
import type { CellMeta } from '../../lib/cells';
import type { BindingContext } from '../../lib/bindings';
import type { EnrichedSubscription } from '../../lib/investor';
import { api } from '@rmint/shell';

type Props = {
  cell: CellMeta;
  contractId?: string;
  investmentId?: string;
  investment?: EnrichedSubscription | null;
  walletAddress?: string;
  investorId?: string;
  /** Audience for the agent render. Defaults to 'investor'. */
  audience?: 'investor' | 'officer' | 'admin';
};

// ─── Agent render result ──────────────────────────────────────────

type AgentRenderResult =
  | { ok: true; markdown: string }
  | { ok: false; errorKind: AgentErrorKind; detail?: string };

type AgentErrorKind = 'auth' | 'forbidden' | 'agent_unavailable' | 'timeout' | 'error';

type AgentApiData = { ok?: boolean; markdown?: string; error?: string; detail?: string } | null;

async function loadCellViaAgent(
  cell: CellMeta,
  contractId: string | undefined,
  investmentId: string | undefined,
  audience: 'investor' | 'officer' | 'admin',
): Promise<AgentRenderResult> {
  // Resolve the primary template path using the cell registry's resolvePaths()
  // function. This keeps slug-to-dir and sequence-prefix knowledge co-located
  // in cells.ts (where CONTRACT_DIRS already lives) and keeps the agent ignorant
  // of both. The first candidate from resolvePaths() is the primary path.
  const candidatePaths = cell.resolvePaths({ contractId, investmentId });
  const templatePath = candidatePaths[0];

  const cellName = cell.name;
  const body: Record<string, unknown> = { cellName, audience };
  if (templatePath !== undefined) body.templatePath = templatePath;
  if (contractId !== undefined) body.contractId = contractId;
  if (investmentId !== undefined) body.investmentId = investmentId;

  let status: number;
  let data: AgentApiData;
  try {
    const res = await api.post<AgentApiData>('/agent/render-cell', body, { responseType: 'json' });
    status = res.status;
    data = res.data ?? null;
  } catch (e) {
    return {
      ok: false,
      errorKind: 'error',
      detail: e instanceof Error ? e.message : String(e),
    };
  }

  if (status === 401) {
    return { ok: false, errorKind: 'auth' };
  }
  if (status === 403) {
    return { ok: false, errorKind: 'forbidden', detail: data?.detail ?? data?.error };
  }
  if (status === 502 || status === 504) {
    return {
      ok: false,
      errorKind: status === 504 ? 'timeout' : 'agent_unavailable',
      detail: data?.detail ?? data?.error,
    };
  }
  if (status !== 200) {
    return {
      ok: false,
      errorKind: 'error',
      detail: data?.detail ?? data?.error ?? `HTTP ${status}`,
    };
  }

  if (data?.ok === true && typeof data.markdown === 'string') {
    return { ok: true, markdown: data.markdown };
  }
  return { ok: false, errorKind: 'error', detail: 'unexpected agent response shape' };
}

// ─── Component ───────────────────────────────────────────────────

export function CellHost({
  cell,
  contractId,
  investmentId,
  walletAddress,
  audience = 'investor',
}: Props) {
  const [result, setResult] = useState<AgentRenderResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    loadCellViaAgent(cell, contractId, investmentId, audience).then((r) => {
      if (!cancelled) setResult(r);
    });
    return () => {
      cancelled = true;
    };
  }, [cell.name, contractId, investmentId, audience]);

  return (
    <article className="max-w-3xl mx-auto py-6 px-2">
      <header className="mb-6 pb-4 border-b border-border">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-mute">
          {scopeLabel(cell, { contractId, investmentId })}
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-foreground">{cell.title}</h1>
      </header>

      {result === null ? (
        <Loading />
      ) : result.ok ? (
        <Body markdown={result.markdown} walletAddress={walletAddress} />
      ) : (
        <AgentErrorView kind={result.errorKind} detail={result.detail} />
      )}
    </article>
  );
}

function Body({
  markdown,
  walletAddress,
}: {
  markdown: string;
  walletAddress: string | undefined;
}) {
  // §6.5: The agent resolves all $placeholder bindings server-side.
  // The frontend no longer calls applyBindings on the response.
  return (
    <>
      <div data-testid="cell-body">
        <MarkdownRenderer walletAddress={walletAddress}>{markdown}</MarkdownRenderer>
      </div>
      <footer className="mt-12 pt-4 border-t border-border text-[11px] text-foreground-mute font-mono">
        source: agent/render-cell
      </footer>
    </>
  );
}

function AgentErrorView({ kind, detail }: { kind: AgentErrorKind; detail?: string }) {
  const messages: Record<AgentErrorKind, string> = {
    auth: 'You need to sign in to view this cell.',
    forbidden: detail ?? "You don't have access to this cell.",
    agent_unavailable: 'Agent unavailable — please retry in a moment.',
    timeout: 'Agent timed out — please retry.',
    error: detail ?? 'An error occurred loading this cell.',
  };

  if (kind === 'auth') {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 space-y-3">
        <p className="text-sm text-foreground-mute">{messages.auth}</p>
        <p className="text-sm">
          <Link to="/" className="text-primary underline underline-offset-2">
            ← back to dashboard
          </Link>
        </p>
      </div>
    );
  }

  const isRetryable = kind === 'agent_unavailable' || kind === 'timeout';
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive space-y-2">
      <p>{messages[kind]}</p>
      {detail && kind !== 'forbidden' ? (
        <p className="text-xs opacity-70 font-mono">{detail}</p>
      ) : null}
      {isRetryable ? (
        <p className="text-xs">
          Tip: make sure the agent is running (
          <code className="font-mono">bun run --cwd apps/agent dev</code>).
        </p>
      ) : null}
    </div>
  );
}

function Loading() {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-4 bg-muted/50 rounded animate-pulse" />
      ))}
    </div>
  );
}

function scopeLabel(
  cell: CellMeta,
  { contractId, investmentId }: { contractId?: string; investmentId?: string },
): string {
  if (cell.scope === 'account') return 'My Account';
  if (cell.scope === 'offering') return contractId ? `Offering · ${contractId}` : 'Offerings';
  if (cell.scope === 'investment') {
    return investmentId
      ? `Investment · ${truncate(investmentId, 16)}`
      : 'My Investments';
  }
  return cell.scope;
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1))}…`;
}

// Re-exported for callers that build bindings externally (unchanged API).
export type CellBindingContext = BindingContext;
